import * as THREE from 'three';

// Biome material layers, in array-index order. The terrain shader selects and blends
// between these by depth. Files live in static/assets/terrain (CC0, ambientCG).
const LAYERS = ['dirt', 'clay', 'rock', 'darkrock'] as const;

export interface TerrainTextureSet {
  readonly albedo: THREE.DataArrayTexture;
  readonly normal: THREE.DataArrayTexture;
  readonly roughness: THREE.DataArrayTexture;
  readonly layerCount: number;
  dispose(): void;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load ${url}`));
    image.src = url;
  });
}

/**
 * Decode each material's map into one layer of a WebGL2 2D texture array, so the terrain
 * shader can triplanar-sample real dirt/rock/normal/roughness on the UV-less deformable mesh.
 * Returns null if anything fails, so the caller can fall back to the procedural material.
 */
export async function loadTerrainTextures(size: number, anisotropy: number): Promise<TerrainTextureSet | null> {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    const build = async (suffix: string, srgb: boolean): Promise<THREE.DataArrayTexture> => {
      const bytesPerLayer = size * size * 4;
      const data = new Uint8Array(bytesPerLayer * LAYERS.length);
      for (let layer = 0; layer < LAYERS.length; layer++) {
        const image = await loadImage(`/assets/terrain/${LAYERS[layer]}_${suffix}.jpg`);
        context.clearRect(0, 0, size, size);
        context.drawImage(image, 0, 0, size, size);
        const pixels = context.getImageData(0, 0, size, size).data;
        data.set(pixels, layer * bytesPerLayer);
      }
      const texture = new THREE.DataArrayTexture(data, size, size, LAYERS.length);
      texture.format = THREE.RGBAFormat;
      texture.type = THREE.UnsignedByteType;
      texture.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
      texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearMipmapLinearFilter;
      texture.generateMipmaps = true;
      texture.anisotropy = anisotropy;
      texture.needsUpdate = true;
      return texture;
    };

    const [albedo, normal, roughness] = await Promise.all([
      build('col', true),
      build('nrm', false),
      build('rgh', false),
    ]);
    return {
      albedo, normal, roughness, layerCount: LAYERS.length,
      dispose() { albedo.dispose(); normal.dispose(); roughness.dispose(); },
    };
  } catch (error) {
    console.warn('Terrain PBR textures could not load; using procedural terrain material.', error);
    return null;
  }
}
